import React from "react";

/*
 * ErrorBoundary — turns a white screen into a readable error.
 *
 * WHY THIS EXISTS
 * There was no boundary anywhere in the app, so any uncaught error during
 * render unmounted the whole React tree and left an empty <div id="root"> —
 * a blank white screen with no clue what happened.
 *
 * That is invisible in local dev (CRA's red overlay catches it first) but is
 * exactly what QA reported inside the native WebView, where there is no
 * overlay and no console. Same crash, two completely different symptoms.
 *
 * STYLING IS INLINE ON PURPOSE.
 * A CSS-module class that does not exist resolves to `undefined` and renders
 * unstyled — we have hit that twice already on this project. The one component
 * whose job is to show up when everything else broke must not depend on a
 * stylesheet resolving.
 *
 * THE STACK IS ALWAYS AVAILABLE, BUT COLLAPSED.
 * Deliberately not gated on NODE_ENV: CRA sets NODE_ENV=production for every
 * `npm run build`, including staging, so gating on it would hide the details
 * exactly where they are needed. Instead it sits inside a collapsed <details>
 * — invisible to a normal user, one tap away for QA on a real device.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, componentStack: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Still log — remote debugging (chrome://inspect / Safari Develop) picks
    // this up, and it carries the component stack the UI below also shows.
    console.error("[ErrorBoundary]", error, info?.componentStack);
    this.setState({ componentStack: info?.componentStack || null });
  }

  handleReload = () => {
    try {
      window.location.reload();
    } catch (_e) {
      /* nothing else we can do */
    }
  };

  handleHome = () => {
    try {
      // Hard navigation, not react-router: the router is part of the tree that
      // just crashed, so a SPA navigate cannot be trusted here.
      window.location.replace("/fast-agent-detail");
    } catch (_e) {
      /* nothing else we can do */
    }
  };

  render() {
    const { error, componentStack } = this.state;
    if (!error) return this.props.children;

    const detail = [
      String(error?.stack || error?.message || error),
      componentStack ? `\nComponent stack:${componentStack}` : "",
    ].join("");

    return (
      <div
        style={{
          minHeight: "100dvh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 16,
          padding: 24,
          boxSizing: "border-box",
          fontFamily: "system-ui, -apple-system, sans-serif",
          color: "#24252c",
          background: "#fff",
          textAlign: "center",
        }}
        role="alert"
      >
        <div style={{ fontSize: 40, lineHeight: 1 }} aria-hidden="true">
          ⚠️
        </div>

        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>
          Something went wrong
        </h1>

        <p style={{ margin: 0, fontSize: 14, color: "#6E6A7C", maxWidth: 360 }}>
          This screen hit an unexpected error. Reloading usually fixes it.
        </p>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
          <button
            type="button"
            onClick={this.handleReload}
            style={{
              background: "#6524EB",
              color: "#fff",
              border: "none",
              borderRadius: 999,
              padding: "12px 22px",
              fontSize: 15,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Reload
          </button>
          <button
            type="button"
            onClick={this.handleHome}
            style={{
              background: "#fff",
              color: "#6524EB",
              border: "1px solid #E5DFFA",
              borderRadius: 999,
              padding: "12px 22px",
              fontSize: 15,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Go to dashboard
          </button>
        </div>

        {/* Collapsed by default — a normal user never opens it, QA taps it on
            the device and gets the stack that used to be unreachable. */}
        <details style={{ width: "100%", maxWidth: 640, textAlign: "left", marginTop: 8 }}>
          <summary style={{ fontSize: 12, color: "#9296A5", cursor: "pointer" }}>
            Technical details
          </summary>
          <pre
            style={{
              marginTop: 8,
              padding: 12,
              background: "#F7F6FB",
              border: "1px solid #EEF0F4",
              borderRadius: 8,
              fontSize: 11,
              lineHeight: 1.45,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              overflowX: "auto",
              maxHeight: "40vh",
            }}
          >
            {detail}
          </pre>
        </details>
      </div>
    );
  }
}

export default ErrorBoundary;
